/*
 * Copyright (c) 2019 Signum+ Software & Facility GmbH
 * Author: jahn@signum-plus.at
 */

import { Injectable } from '@angular/core';
import {
  TextModel,
  TextInputModel,
  ViewComponentKey,
  ChangeModes
} from '@typescript-kit/view';
import { ViewService } from '@angular-kit/view';
import { TimeRecordingViewModel } from '../view-model/time-recording.view-model';
import { ChangedEvent, ObservableMap } from '@typescript-kit/core';
import { TimeRecordingMenuComponentModel } from '../component-model/time-recording-menu.component-model';
import { TimeRecordingViewKey } from '../key/time-recording-view.key';

@Injectable()
export class EmployeeTimeRecordingMenuViewService extends ViewService<TimeRecordingViewModel, TimeRecordingMenuComponentModel> {
  protected onViewModelPropertyChanged(event: ChangedEvent): void {
    if (event.originalEvent !== event) {
      if (event.originalEvent.source === this.viewModel.monthComment) {
        this.onMonthCommentPropertyChanged(event.originalEvent);
      }
      return;
    }
    super.onViewModelPropertyChanged(event);
  }

  private onMonthCommentPropertyChanged(event: ChangedEvent) {
    this.refreshComponentModel(this.componentModel);
  }

  protected createComponentModel(): TimeRecordingMenuComponentModel {
    return new TimeRecordingMenuComponentModel({
      items: new ObservableMap({
        employeeName: this.createEmployeeNameViewModel(),
        monthText: this.createMonthTextModel(),
        monthComment: this.createMonthCommentViewModel(),
      })
    });
  }

  protected refreshComponentModel(componentModel: TimeRecordingMenuComponentModel) {
    this.refreshEmployeeNameViewModel(componentModel.employeeName);
    this.refreshMonthTextModel(componentModel.monthText);
    this.refreshMonthCommentViewModel(componentModel.monthComment);
  }

  private createEmployeeNameViewModel() {
    const model = new TextModel({
      key: 'title', tags: ['app-time-recording-title'],
    });
    this.refreshEmployeeNameViewModel(model);
    return model;
  }

  private refreshEmployeeNameViewModel(model: TextModel) {
    model.setValues({
      isHidden: !this.viewModel.printView,
      text: this.getEmployeeName(),
    });
  }

  private createMonthTextModel(): TextModel {
    const textModel = new TextModel({});
    this.refreshMonthTextModel(textModel);
    return textModel;
  }

  private refreshMonthTextModel(model: TextModel) {
    const month = this.viewModel.month ? this.textService.getText(`shared/model/month/value/${this.viewModel.month}`) : '';
    const year = this.viewModel.year ? this.viewModel.year.toString() : '';
    model.setValues({
      isHidden: !this.viewModel.printView,
      text: `${month} ${year}`,
    });
  }

  private getEmployeeName() {
    if (!this.viewModel.employeeList || !this.viewModel.employeeId) {
      return null;
    }
    const currentEmployee = this.viewModel.employeeList.find(employee => employee.id === this.viewModel.employeeId);
    return currentEmployee && currentEmployee.name;
  }

  private createMonthCommentViewModel(): TextInputModel {
    const model = new TextInputModel({
      key: 'monthComment', scope: TimeRecordingViewKey.TIME_RECORDING_MENU,
      type: ViewComponentKey.TEXT_AREA,
      autoselect: false,
      changeMode: ChangeModes.DELAYED,
      changeDelay: 1000,
      value: null,
      onValueChanged: (value) => {
        this.viewModel.monthComment.value = value;
      }
    });
    this.refreshMonthCommentViewModel(model);
    return model;
  }

  private refreshMonthCommentViewModel(model: TextInputModel) {
    model.setValues({
      isHidden: !this.viewModel.employeeId || !this.viewModel.year || !this.viewModel.month,
      value: this.viewModel.monthComment ? this.viewModel.monthComment.value : null,
    });
  }
}
